import React, { useEffect, useMemo, useRef, useState } from "react";
import { OrderServices } from "../../services/OrderServices";
import { DeliveryAddressServices } from "../../services/deliveryAddressServices";

const emptyForm = {
  first_name: "",
  last_name: "",
  business_name: "",
  country_id: "",
  address: "",
  address_line2: "",
  city: "",
  state_id: "",
  post_code: "",
  phone: "",
  email: "",
};

const normalizeList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const getLocationLabel = (loc) =>
  loc?.name || loc?.country_name || loc?.location_name || `Country #${loc?.id}`;

const getStateLabel = (st) =>
  st?.state_name || st?.name || `State #${st?.id}`;

const getStateLocationId = (st) =>
  st?.location_id ?? st?.country_id ?? st?.location?.id ?? "";

const DeliveryAddress = ({ onBack }) => {
  const deliveryRootRef = useRef(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      deliveryRootRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  const [form, setForm] = useState(emptyForm);
  const [addressId, setAddressId] = useState(null);

  const [locations, setLocations] = useState([]);
  const [states, setStates] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const filteredStates = useMemo(() => {
    return states.filter(
      (st) => String(getStateLocationId(st)) === String(form.country_id)
    );
  }, [states, form.country_id]);

  const selectedCountry = useMemo(() => {
    return locations.find((loc) => String(loc.id) === String(form.country_id));
  }, [locations, form.country_id]);

  const selectedState = useMemo(() => {
    return filteredStates.find((st) => String(st.id) === String(form.state_id));
  }, [filteredStates, form.state_id]);

  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      setError("");

      try {
        setLocationLoading(true);

        const [locationData, stateData, addressRes] = await Promise.all([
          OrderServices.getLocations(),
          OrderServices.getStates(),
          DeliveryAddressServices.getAddress(),
        ]);

        const savedAddress = addressRes?.data;

        const allLocations = normalizeList(locationData);
        const activeLocations = allLocations.filter((loc) => {
          if (loc?.is_active === undefined || loc?.is_active === null) return true;
          return loc?.is_active === true || loc?.is_active === 1 || loc?.is_active === "1";
        });

        const allStates = normalizeList(stateData);

        const savedCountryId =
          savedAddress?.country_id ?? savedAddress?.country?.id ?? "";
        const savedStateId =
          savedAddress?.state_id ?? savedAddress?.state?.id ?? "";

        const locationsWithSaved =
          savedAddress?.country &&
          savedCountryId &&
          !activeLocations.some((loc) => String(loc.id) === String(savedCountryId))
            ? [...activeLocations, savedAddress.country]
            : activeLocations;

        const statesWithSaved =
          savedAddress?.state &&
          savedStateId &&
          !allStates.some((st) => String(st.id) === String(savedStateId))
            ? [...allStates, savedAddress.state]
            : allStates;

        setLocations(locationsWithSaved);
        setStates(statesWithSaved);

        if (savedAddress) {
          setAddressId(savedAddress.id);

          setForm({
            first_name: savedAddress.first_name || "",
            last_name: savedAddress.last_name || "",
            business_name: savedAddress.business_name || "",
            country_id: savedCountryId ? String(savedCountryId) : "",
            address: savedAddress.address || "",
            address_line2: savedAddress.address_line2 || "",
            city: savedAddress.city || "",
            state_id: savedStateId ? String(savedStateId) : "",
            post_code: savedAddress.post_code || "",
            phone: savedAddress.phone || "",
            email: savedAddress.email || "",
          });
        }
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Failed to load delivery address details."
        );
      } finally {
        setLocationLoading(false);
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const handleField = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      if (name === "country_id") {
        return {
          ...prev,
          country_id: value,
          state_id: "",
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });

    setError("");
    setSuccess("");
  };

  const validateForm = () => {
    if (!form.first_name.trim()) return "First name is required.";
    if (!form.last_name.trim()) return "Last name is required.";
    if (!form.country_id) return "Country is required.";
    if (!form.address.trim()) return "Address is required.";
    if (!form.city.trim()) return "City is required.";
    if (!form.state_id) return "State is required.";
    if (!form.post_code.trim()) return "Post code is required.";
    if (!form.phone.trim()) return "Phone is required.";
    if (!form.email.trim()) return "Email is required.";

    return "";
  };

  const buildPayload = () => ({
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    business_name: form.business_name.trim() || null,
    country_id: Number(form.country_id),
    address: form.address.trim(),
    address_line2: form.address_line2.trim() || null,
    city: form.city.trim(),
    state_id: Number(form.state_id),
    post_code: form.post_code.trim(),
    phone: String(form.phone).trim(),
    email: form.email.trim(),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccess("");

    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);

    try {
      const payload = buildPayload();

      let res;

      if (addressId) {
        res = await DeliveryAddressServices.updateAddress(addressId, payload);
        setSuccess("Delivery address updated successfully.");
      } else {
        res = await DeliveryAddressServices.addAddress(payload);
        setAddressId(res?.data?.id || null);
        setSuccess("Delivery address added successfully.");
      }

      if (res?.data) {
        setForm({
          first_name: res.data.first_name || "",
          last_name: res.data.last_name || "",
          business_name: res.data.business_name || "",
          country_id: res.data.country_id ? String(res.data.country_id) : "",
          address: res.data.address || "",
          address_line2: res.data.address_line2 || "",
          city: res.data.city || "",
          state_id: res.data.state_id ? String(res.data.state_id) : "",
          post_code: res.data.post_code || "",
          phone: res.data.phone || "",
          email: res.data.email || "",
        });

        setAddressId(res.data.id || addressId);
      }
    } catch (err) {
      const backendErrors = err?.response?.data?.errors;

      if (backendErrors) {
        const firstError = Object.values(backendErrors).flat()[0];
        setError(firstError);
      } else {
        setError(
          err?.response?.data?.message ||
            "Failed to save delivery address. Please try again."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="da-card" ref={deliveryRootRef}>
        <style>{CSS}</style>
        <div className="da-loading">
          <i className="ph ph-spinner-gap da-spin" />
          Loading delivery address...
        </div>
      </div>
    );
  }

  return (
    <div className="da-card" ref={deliveryRootRef}>
      <style>{CSS}</style>

      <div className="da-header">
        <div>
          <button
            type="button"
            className="da-back-btn"
            onClick={onBack}
          >
            <i className="ph ph-arrow-left" />
            Back to Overview
          </button>

          <div className="da-title">
            {addressId ? "Update Delivery Address" : "Add Delivery Address"}
          </div>
          <div className="da-subtitle">
            {addressId
              ? "Your saved address is loaded below. You can update it anytime."
              : "Add your delivery address. You can save only one address."}
          </div>
        </div>

        <div className={`da-status ${addressId ? "da-status--saved" : ""}`}>
          {addressId ? "Saved" : "New"}
        </div>
      </div>

      {error && (
        <div className="da-alert da-alert--error">
          <i className="ph ph-warning-circle" />
          {error}
        </div>
      )}

      {success && (
        <div className="da-alert da-alert--success">
          <i className="ph ph-check-circle" />
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="da-grid">
          <input
            className="da-input"
            name="first_name"
            value={form.first_name}
            onChange={handleField}
            placeholder="First Name *"
          />

          <input
            className="da-input"
            name="last_name"
            value={form.last_name}
            onChange={handleField}
            placeholder="Last Name *"
          />

          <input
            className="da-input da-col-2"
            name="business_name"
            value={form.business_name}
            onChange={handleField}
            placeholder="Business Name (optional)"
          />

          <select
            className="da-input"
            name="country_id"
            value={form.country_id}
            onChange={handleField}
            disabled={locationLoading}
          >
            <option value="">
              {locationLoading ? "Loading countries..." : "Select Country / Region *"}
            </option>
            {locations.map((loc) => (
              <option key={loc.id} value={String(loc.id)}>
                {getLocationLabel(loc)}
              </option>
            ))}
          </select>

          <select
            className="da-input"
            name="state_id"
            value={form.state_id}
            onChange={handleField}
            disabled={!form.country_id || filteredStates.length === 0}
          >
            <option value="">
              {!form.country_id
                ? "Select country first *"
                : filteredStates.length === 0
                ? "No states available"
                : "Select State / Province *"}
            </option>
            {filteredStates.map((st) => (
              <option key={st.id} value={String(st.id)}>
                {getStateLabel(st)}
              </option>
            ))}
          </select>

          {(selectedCountry || selectedState) && (
            <div className="da-selected-location da-col-2">
              <span>
                <strong>Country:</strong> {selectedCountry ? getLocationLabel(selectedCountry) : "—"}
              </span>
              <span>
                <strong>State:</strong> {selectedState ? getStateLabel(selectedState) : "—"}
              </span>
            </div>
          )}

          <input
            className="da-input da-col-2"
            name="address"
            value={form.address}
            onChange={handleField}
            placeholder="Street Address *"
          />

          <input
            className="da-input da-col-2"
            name="address_line2"
            value={form.address_line2}
            onChange={handleField}
            placeholder="Apt, suite, unit, etc. (optional)"
          />

          <input
            className="da-input"
            name="city"
            value={form.city}
            onChange={handleField}
            placeholder="Town / City *"
          />

          <input
            className="da-input"
            name="post_code"
            value={form.post_code}
            onChange={handleField}
            placeholder="ZIP / Post Code *"
          />

          <input
            className="da-input"
            name="phone"
            value={form.phone}
            onChange={handleField}
            placeholder="Phone *"
          />

          <input
            className="da-input"
            name="email"
            type="email"
            value={form.email}
            onChange={handleField}
            placeholder="Email Address *"
          />
        </div>

        <button type="submit" className="da-submit" disabled={saving}>
          {saving ? (
            <>
              <i className="ph ph-spinner-gap da-spin" />
              Saving...
            </>
          ) : addressId ? (
            "Update Address"
          ) : (
            "Add Address"
          )}
        </button>
      </form>
    </div>
  );
};

const CSS = `
  .da-card {
    font-family: 'Syne', sans-serif;
    background: #fff;
    border: 1px solid #f3f4f6;
    border-radius: 14px;
    padding: 24px;
    max-width: 860px;
    width: 100%;
    margin: 0 auto;
  }

  .da-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 20px;
  }

  .da-title {
    font-size: 20px;
    font-weight: 800;
    color: #111827;
  }

  .da-subtitle {
    font-size: 13px;
    color: #6b7280;
    margin-top: 4px;
    line-height: 1.5;
  }

  .da-status {
    padding: 5px 11px;
    border-radius: 999px;
    background: #fef3c7;
    color: #92400e;
    font-size: 11px;
    font-weight: 800;
  }

  .da-status--saved {
    background: #dcfce7;
    color: #166534;
  }

  .da-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
  }

  .da-col-2 {
    grid-column: span 2;
  }

  .da-input {
    width: 100%;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 13.5px;
    color: #111827;
    outline: none;
    background: #fff;
    transition: border .15s, box-shadow .15s;
  }

  .da-input:focus {
    border-color: #f27b21;
    box-shadow: 0 0 0 3px rgba(242, 123, 33, .12);
  }

  .da-input::placeholder {
    color: #9ca3af;
  }

  .da-input:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }

  .da-selected-location {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f9fafb;
    border: 1px dashed #e5e7eb;
    color: #374151;
    font-size: 12px;
    line-height: 1.5;
  }

  .da-selected-location span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .da-submit {
    margin-top: 18px;
    width: 100%;
    border: none;
    border-radius: 10px;
    padding: 13px 16px;
    background: #111827;
    color: #fff;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
  }

  .da-submit:hover:not(:disabled) {
    background: #000;
  }

  .da-submit:disabled {
    opacity: .65;
    cursor: not-allowed;
  }
  .da-back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    background: #f9fafb;
    color: #374151;
    font-size: 12px;
    font-weight: 800;
    border-radius: 8px;
    padding: 8px 12px;
    margin-bottom: 14px;
    cursor: pointer;
    font-family: 'Syne', sans-serif;
  }

  .da-back-btn:hover {
    background: #f3f4f6;
    color: #111827;
  }

  .da-back-btn .ph {
    font-size: 14px;
  }

  .da-alert {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 11px 13px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    margin-bottom: 16px;
  }

  .da-alert--error {
    background: #fef2f2;
    color: #b91c1c;
    border: 1px solid #fecaca;
  }

  .da-alert--success {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
  }

  .da-loading {
    min-height: 140px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    color: #6b7280;
    font-size: 13px;
    font-weight: 700;
  }

  @keyframes da-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .da-spin {
    display: inline-block;
    animation: da-spin .75s linear infinite;
  }

  @media (max-width: 640px) {
    .da-card {
      padding: 18px;
      border-radius: 12px;
    }

    .da-header {
      flex-direction: column;
      align-items: stretch;
    }

    .da-grid {
      grid-template-columns: 1fr;
    }

    .da-col-2 {
      grid-column: span 1;
    }

    .da-title {
      font-size: 18px;
    }
  }
`;

export default DeliveryAddress;